import type { GridApi } from 'ag-grid-community'
import type { Cleanup } from '../../../helpers/cleanup'

// Constants -------------------------------------------------------------------

const MAIN_VIEW = '[data-phx-main]'
const TEARDOWN_TIMEOUT_MS = 45000
const HANDOFF_TIMEOUT_MS = 1000

// Private functions -----------------------------------------------------------

/** Only a connected replacement can take over this document's grid styles. */
const hasPendingGrid = (document: Document): boolean =>
  [...pendingInitializations].some(
    (element) => element.ownerDocument === document && element.isConnected
  )

// Public functions ------------------------------------------------------------

/** Retain outgoing styles until scheduled grid creation completes or is canceled. */
export const beginGridInitialization = (element: HTMLElement): Cleanup => {
  pendingInitializations.add(element)

  /** Settle this initialization once, including cancellation or page departure. */
  const finish = (): void => {
    if (!pendingInitializations.delete(element)) {
      return
    }

    window.removeEventListener('pagehide', finish)

    for (const checkRemoval of removalChecks) {
      checkRemoval()
    }
  }

  window.addEventListener('pagehide', finish, { once: true })

  return finish
}

/** Retain the visible outgoing grid, then briefly hand its styles to a replacement. */
export const destroyGridAfterRemoval = (
  element: HTMLElement,
  grid: Pick<GridApi<unknown>, 'destroy' | 'stopEditing'>
): void => {
  if (
    element.isConnected
      ? !element.closest(MAIN_VIEW)
      : !hasPendingGrid(element.ownerDocument)
  ) {
    grid.destroy()
    return
  }

  grid.stopEditing(true)
  element.inert = true
  let phase: 'visible' | 'handoff' | 'destroyed' = 'visible'

  /** Finish teardown once and release every observer, listener, and timer. */
  const destroy = (): void => {
    if (phase === 'destroyed') {
      return
    }

    phase = 'destroyed'
    observer.disconnect()
    clearTimeout(timeout)
    removalChecks.delete(checkRemoval)
    window.removeEventListener('pagehide', destroy)
    grid.destroy()
  }

  /** Release detached grids unless a replacement is waiting for its first frame. */
  const checkRemoval = (): void => {
    if (phase === 'destroyed' || element.isConnected) {
      return
    }

    if (!hasPendingGrid(element.ownerDocument)) {
      destroy()
      return
    }

    if (phase === 'visible') {
      phase = 'handoff'
      clearTimeout(timeout)
      timeout = setTimeout(destroy, HANDOFF_TIMEOUT_MS)
    }
  }

  const observer = new MutationObserver(checkRemoval)
  let timeout = setTimeout(destroy, TEARDOWN_TIMEOUT_MS)
  removalChecks.add(checkRemoval)
  observer.observe(element.ownerDocument.body, {
    childList: true,
    subtree: true
  })
  window.addEventListener('pagehide', destroy, { once: true })
  checkRemoval()
}

// Module setup ----------------------------------------------------------------

const pendingInitializations = new Set<HTMLElement>()
const removalChecks = new Set<Cleanup>()
